import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UsersThree, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function InviteAlumniDialog({ eventId, institutionName }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ grad_year: "", course: "", degree: "", message: "" });
  const [aud, setAud] = useState(null);
  const [facets, setFacets] = useState({ grad_years: [], courses: [] });
  const [sending, setSending] = useState(false);

  const preview = (params) => {
    api.get(`/events/${eventId}/invite-audience`, { params }).then((r) => {
      setAud(r.data);
      if (r.data.facets) setFacets(r.data.facets);
    }).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  };
  useEffect(() => { if (open) preview({}); /* eslint-disable-next-line */ }, [open]);
  const set = (k, v) => {
    const val = v === "any" ? "" : v;
    const next = { ...f, [k]: val };
    setF(next);
    preview({ grad_year: next.grad_year, course: next.course, degree: next.degree });
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/events/${eventId}/invite`, f);
      toast.success(r.data.invited > 0 ? `Invited ${r.data.invited} alumni 🎉` : "No new alumni to invite (all already invited)");
      setOpen(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full gap-1.5" data-testid="invite-alumni-btn"><UsersThree size={16} /> Invite alumni</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-2xl font-light">Invite alumni</DialogTitle></DialogHeader>
        <DialogDescription className="-mt-2 text-sm text-muted-foreground">Notify {institutionName} alumni by batch, course or graduation year.</DialogDescription>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label className="text-xs">Graduation year</Label>
              <Select value={f.grad_year || "any"} onValueChange={(v) => set("grad_year", v)}>
                <SelectTrigger data-testid="invite-gradyear"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className="max-h-60"><SelectItem value="any">Any year</SelectItem>{facets.grad_years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Course</Label>
              {facets.courses.length > 0 ? (
                <Select value={f.course || "any"} onValueChange={(v) => set("course", v)}>
                  <SelectTrigger data-testid="invite-course"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent className="max-h-60"><SelectItem value="any">Any course</SelectItem>{facets.courses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Input value={f.course} onChange={(e) => set("course", e.target.value)} placeholder="Any" data-testid="invite-course-input" />}
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Degree</Label>
              <Input value={f.degree} onChange={(e) => set("degree", e.target.value)} placeholder="Any" data-testid="invite-degree-input" />
            </div>
          </div>

          <div className="rounded-xl border border-border/60 bg-muted/40 p-4" data-testid="invite-audience-box">
            <p className="text-sm"><span className="text-2xl font-semibold text-primary">{aud ? aud.total : "…"}</span> <span className="text-muted-foreground">alumni match</span></p>
            {aud && aud.sample?.length > 0 && <p className="mt-1 text-xs text-muted-foreground">e.g. {aud.sample.slice(0, 5).join(", ")}{aud.total > 5 ? "…" : ""}</p>}
            {aud && aud.already_invited > 0 && <p className="mt-1 text-xs text-muted-foreground">{aud.already_invited} already invited</p>}
          </div>

          <div className="space-y-1.5"><Label className="text-xs">Personal message (optional)</Label>
            <Textarea rows={2} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="We'd love to see you back on campus…" data-testid="invite-message" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={send} disabled={sending || !aud || aud.total === 0} className="rounded-full gap-1.5" data-testid="invite-send">
            <PaperPlaneTilt size={16} /> {sending ? "Sending…" : `Send ${aud && aud.total ? aud.total : ""} invites`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
